import React from 'react';
import {
  ClipboardCheck,
  Plus,
  Eye,
  Edit3,
  Trash2,
  Filter,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building,
  User,
  Calendar,
  FileText
} from 'lucide-react';

const HSEAssessmentList = ({
  assessments,
  isLoading,
  error,
  success,
  statistics,
  filters,
  setFilters,
  properties,
  users,
  user,
  onCreateNew,
  onViewAssessment,
  onEditAssessment,
  onDeleteAssessment,
  onSubmitForReview,
  onApproveAssessment,
  onRejectAssessment,
  getStatusColor,
  getComplianceColor,
  BACKEND_URL,
  setSelectedAssessment,
  setAssessmentForm,
  setUploadedImages,
  setFieldImages,
  setAssessmentRequirements,
  setActiveView,
  resetForm
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <ClipboardCheck className="mr-2 h-5 w-5 text-blue-500" />
            HSE Assessments
          </h2>
          <p className="text-gray-600 mt-1">Manage tenant health and safety assessments</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setActiveView('create');
          }}
          className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]"
        >
          <Plus size={16} />
          New Assessment
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Statistics (Admin/Manager only) */}
      {statistics && (user?.role?.value === 'Admin' || user?.role?.value === 'Property Manager') && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Assessments</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total_assessments}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Average Score</p>
                <p className={`text-2xl font-bold ${getComplianceColor(statistics.average_compliance_score)}`}>
                  {statistics.average_compliance_score}%
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.assessments_this_month}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Follow-ups</p>
                <p className="text-2xl font-bold text-orange-600">{statistics.pending_follow_ups}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400" />
            <span className="text-base sm:text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm w-full min-h-[48px] sm:min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <input
              type="text"
              placeholder="Property Name"
              value={filters.property_name || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, property_name: e.target.value }))}
              className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm w-full min-h-[48px] sm:min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label className="flex items-center text-base sm:text-sm p-4 sm:p-3 border border-gray-300 rounded-lg min-h-[48px] sm:min-h-[44px] cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={filters.my_assessments}
                onChange={(e) => setFilters(prev => ({ ...prev, my_assessments: e.target.checked }))}
                className="mr-3 w-5 h-5 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="select-none">My Assessments Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Assessments List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">
            Assessments ({assessments.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <div className="flex flex-col items-center space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-sm sm:text-base">Loading assessments...</span>
              </div>
            </div>
          ) : assessments.length === 0 ? (
            <div className="p-6 sm:p-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Filter className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">
                    {(filters.status || filters.building_id || filters.my_assessments)
                      ? 'No assessments match your filters'
                      : 'No assessments found'
                    }
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 max-w-sm mx-auto">
                    {(filters.status || filters.building_id || filters.my_assessments)
                      ? 'Try adjusting your filters or create a new assessment to get started.'
                      : 'Create your first HSE assessment to get started.'
                    }
                  </p>
                </div>
                <button
                  onClick={() => {
                    resetForm();
                    setActiveView('create');
                  }}
                  className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 flex items-center gap-2 min-h-[48px] text-base sm:text-sm transition-colors"
                >
                  <Plus size={20} className="sm:w-4 sm:h-4" />
                  New Assessment
                </button>
              </div>
            </div>
          ) : (
            assessments.map((assessment) => (
              <div key={assessment.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col gap-4">
                  {/* Mobile-optimized header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-2">
                        <h4 className="text-base sm:text-lg font-medium text-gray-900 leading-tight">
                          {assessment.tenant_name}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(assessment.status)}`}>
                            {assessment.status}
                          </span>
                          <span className={`text-sm font-medium ${getComplianceColor(assessment.overall_compliance_score)}`}>
                            {assessment.overall_compliance_score}% Compliance
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assessment details - mobile optimized */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Building className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">
                        {properties.find(p => p.id === assessment.building_id)?.building_name ||
                         properties.find(p => p.id === assessment.building_id)?.buildingName ||
                         assessment.building_id}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{assessment.auditor_name}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>{new Date(assessment.assessment_date).toLocaleDateString()}</span>
                    </div>
                    {assessment.follow_up_required && (
                      <div className="flex items-center text-orange-600">
                        <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">
                          Follow-up: {new Date(assessment.follow_up_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons - mobile optimized */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-400">
                      Created {new Date(assessment.created_at).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-1 flex-shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessment.id}`, {
                              credentials: 'include'
                            });
                            if (response.ok) {
                              const full = await response.json();
                              setSelectedAssessment(full);
                            } else {
                              setSelectedAssessment(assessment);
                            }
                          } catch (e) {
                            setSelectedAssessment(assessment);
                          }
                          setActiveView('view');
                        }}
                        className="flex items-center justify-center p-3 sm:p-2 text-gray-400 hover:text-blue-600 min-h-[48px] min-w-[48px] sm:min-h-[44px] sm:min-w-[44px] rounded-lg hover:bg-blue-50 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-5 h-5 sm:w-4 sm:h-4" />
                      </button>
                      {(String(assessment.created_by) === String(user?.id) && assessment.status === 'draft') && (
                        <>
                          <button
                          onClick={async () => {
                            // Fetch full assessment to ensure images are present
                            let fullAssessment = assessment;
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessment.id}`, {
                                credentials: 'include'
                              });
                              if (response.ok) {
                                fullAssessment = await response.json();
                              }
                            } catch (e) {
                              // fall back
                            }

                            setSelectedAssessment(fullAssessment);
                            // Format the assessment_date for the date input field
                            const formattedAssessment = {
                              ...fullAssessment,
                              assessment_date: fullAssessment.assessment_date ?
                                new Date(fullAssessment.assessment_date).toISOString().split('T')[0] :
                                new Date().toISOString().split('T')[0]
                            };
                            setAssessmentForm(formattedAssessment);

                            // Reset existing images state
                            setUploadedImages([]);
                            setFieldImages({});

                            // Helper to push general uploaded images (legacy 'images')
                            const pushLegacyImages = (imagesArr) => {
                              if (imagesArr && Array.isArray(imagesArr)) {
                                const formattedImages = imagesArr.map((img, idx) => {
                                  if (typeof img === 'string') {
                                    return {
                                      id: `existing-${fullAssessment.id}-${idx}`,
                                      filename: `image-${idx + 1}.png`,
                                      content_type: 'image/png',
                                      data: img,
                                      temporary: false
                                    };
                                  }
                                  return img;
                                });
                                setUploadedImages(formattedImages);
                              }
                            };

                            // Map categorized_images back to fieldImages and uploadedImages
                            try {
                              const cat = fullAssessment.categorized_images;
                              if (cat && typeof cat === 'object') {
                                // Support both raw dict {category: [images]} and model-shaped {images: {...}}
                                const categoriesObj = cat.images && typeof cat.images === 'object' ? cat.images : cat;
                                const nextFieldImages = {};
                                const generalBucket = [];

                                // Reverse mapping from backend
                                const categoryToFields = {
                                  legal_wall_charts: ['ohs_act','labour_relations_act','basic_conditions_employment','coida'],
                                  fire_fighting_equipment: ['fire_extinguishers','fire_hose_reels','fire_blankets','emergency_lighting','fire_pump','fire_hydrants','fire_suppression_system','fire_safety_signage'],
                                  automatic_sprinkler_system: ['sprinkler_system_serviced','sprinkler_valves_locked_chain_padlock','sprinkler_valve_chamber_spare_parts','sprinkler_valves_labelled','sprinkler_valve_chamber_schematic'],
                                  emergency_preparedness: ['emergency_assembly_points','emergency_contact_list','emergency_procedures','first_aid_kits','emergency_communication','emergency_drills','emergency_exits','emergency_wardens','backup_power'],
                                  electrical_safety: ['electrical_installations','electrical_testing','electrical_safety_switches','electrical_db'],
                                  hazardous_materials: ['chemical_storage','chemical_handling_procedures','hazardous_chemicals'],
                                  evacuation_routes_exits: ['escape_doors_fire_exit_signage','fire_escape_plans_displayed','evacuation_routes'],
                                  fire_detection_alarm: ['fire_detection_serviced_tested_standalone','fire_detection_operating_procedures_standalone','fire_alarm_call_points_signage_standalone','dust_covers_removed_standalone','fire_detection'],
                                  signage_displayed: ['signage_displayed'],
                                  general_conditions: ['housekeeping','floors','walls','ceilings','windows','doors','stairways','lighting','general_condition'],
                                  stacking_articles: ['stacking_height_limits','article_stacking']
                                };

                                // Build quick lookup from field_name if present
                                const fieldNameKeyed = (img) => {
                                  const fname = img.field_name || '';
                                  const key = fname ? `${fname}_images` : null;
                                  return key;
                                };

                                Object.entries(categoriesObj).forEach(([category, images]) => {
                                  (images || []).forEach((img, idx) => {
                                    const base64 = img.data || img.url || img;
                                    const payload = {
                                      id: img.id || `existing-${fullAssessment.id}-${category}-${idx}`,
                                      filename: img.file_name || img.filename || `image-${idx + 1}.jpg`,
                                      content_type: img.mime_type || img.content_type || 'image/jpeg',
                                      size: img.file_size || img.size || 0,
                                      data: typeof base64 === 'string' && base64.startsWith('data:') ? base64 : base64,
                                      temporary: false,
                                      field: img.field_name || undefined
                                    };

                                    // Prefer explicit field_name mapping
                                    const explicitKey = fieldNameKeyed(img);
                                    if (explicitKey) {
                                      nextFieldImages[explicitKey] = [...(nextFieldImages[explicitKey] || []), payload];
                                      return;
                                    }

                                    // Otherwise derive from category
                                    const candidateFields = categoryToFields[category] || [];
                                    if (candidateFields.length > 0) {
                                      const chosen = candidateFields[0];
                                      const key = `${chosen}_images`;
                                      nextFieldImages[key] = [...(nextFieldImages[key] || []), { ...payload, field: chosen }];
                                    } else {
                                      generalBucket.push(payload);
                                    }
                                  });
                                });

                                setFieldImages(nextFieldImages);
                                // Also surface any non-field images to uploadedImages (general)
                                if (generalBucket.length > 0) {
                                  setUploadedImages(prev => [...prev, ...generalBucket]);
                                }
                              } else {
                                // Fallback to legacy flat images
                                pushLegacyImages(fullAssessment.images);
                              }
                            } catch (e) {
                              // If categorized handling fails, fallback to legacy
                              pushLegacyImages(fullAssessment.images);
                            }

                            // Populate assessment requirements checkboxes based on existing data
                            let requirements = {
                              legal_wall_charts: false,
                              signage_displayed: false,
                              electrical_db: false,
                              fire_fighting_equipment: false,
                              fire_detection_systems: false,
                              stacking_articles: false,
                              automatic_sprinkler: false,
                              evacuation_routes: false,
                              housekeeping: false,
                              emergency_preparedness: false,
                              hazardous_chemicals: false,
                              general_condition: false
                            };

                            try {
                              console.log('Loading assessment requirements for edit:', fullAssessment.id);

                              if (fullAssessment.assessment_requirements && typeof fullAssessment.assessment_requirements === 'object') {
                                requirements = {
                                  legal_wall_charts: fullAssessment.assessment_requirements.legal_wall_charts || false,
                                  signage_displayed: fullAssessment.assessment_requirements.signage_displayed || false,
                                  electrical_db: fullAssessment.assessment_requirements.electrical_db || false,
                                  fire_fighting_equipment: fullAssessment.assessment_requirements.fire_fighting_equipment || false,
                                  fire_detection_systems: fullAssessment.assessment_requirements.fire_detection_systems || false,
                                  stacking_articles: fullAssessment.assessment_requirements.stacking_articles || false,
                                  automatic_sprinkler: fullAssessment.assessment_requirements.automatic_sprinkler || false,
                                  evacuation_routes: fullAssessment.assessment_requirements.evacuation_routes || false,
                                  housekeeping: fullAssessment.assessment_requirements.housekeeping || false,
                                  emergency_preparedness: fullAssessment.assessment_requirements.emergency_preparedness || false,
                                  hazardous_chemicals: fullAssessment.assessment_requirements.hazardous_chemicals || false,
                                  general_condition: fullAssessment.assessment_requirements.general_condition || false
                                };
                              } else {
                                requirements = {
                                  legal_wall_charts: (fullAssessment.ohs_act_compliance && fullAssessment.ohs_act_compliance !== 'not_applicable') ||
                                                   (fullAssessment.labour_relations_act_compliance && fullAssessment.labour_relations_act_compliance !== 'not_applicable') ||
                                                   (fullAssessment.basic_conditions_employment_compliance && fullAssessment.basic_conditions_employment_compliance !== 'not_applicable') ||
                                                   (fullAssessment.coida_compliance && fullAssessment.coida_compliance !== 'not_applicable'),

                                  signage_displayed: (fullAssessment.signage_displayed_compliance && fullAssessment.signage_displayed_compliance !== 'not_applicable'),

                                  electrical_db: (fullAssessment.electrical_db_compliance && fullAssessment.electrical_db_compliance !== 'not_applicable') ||
                                               (fullAssessment.electrical_installations_compliance && fullAssessment.electrical_installations_compliance !== 'not_applicable') ||
                                               (fullAssessment.electrical_testing_compliance && fullAssessment.electrical_testing_compliance !== 'not_applicable') ||
                                               (fullAssessment.electrical_safety_switches_compliance && fullAssessment.electrical_safety_switches_compliance !== 'not_applicable'),

                                  fire_fighting_equipment: (fullAssessment.fire_equipment_compliance && fullAssessment.fire_equipment_compliance !== 'not_applicable') ||
                                                         (fullAssessment.fire_extinguishers_compliance && fullAssessment.fire_extinguishers_compliance !== 'not_applicable') ||
                                                         (fullAssessment.fire_hose_reels_compliance && fullAssessment.fire_hose_reels_compliance !== 'not_applicable') ||
                                                         (fullAssessment.fire_blankets_compliance && fullAssessment.fire_blankets_compliance !== 'not_applicable'),

                                  fire_detection_systems: (fullAssessment.fire_detection_compliance && fullAssessment.fire_detection_compliance !== 'not_applicable') ||
                                                        (fullAssessment.fire_detection_serviced_tested_standalone_compliance && fullAssessment.fire_detection_serviced_tested_standalone_compliance !== 'not_applicable') ||
                                                        (fullAssessment.fire_detection_operating_procedures_standalone_compliance && fullAssessment.fire_detection_operating_procedures_standalone_compliance !== 'not_applicable'),

                                  stacking_articles: (fullAssessment.article_stacking_compliance && fullAssessment.article_stacking_compliance !== 'not_applicable') ||
                                                   (fullAssessment.stacking_height_limits_compliance && fullAssessment.stacking_height_limits_compliance !== 'not_applicable'),

                                  automatic_sprinkler: (fullAssessment.sprinkler_system_compliance && fullAssessment.sprinkler_system_compliance !== 'not_applicable') ||
                                                     (fullAssessment.sprinkler_system_serviced_compliance && fullAssessment.sprinkler_system_serviced_compliance !== 'not_applicable') ||
                                                     (fullAssessment.sprinkler_valves_locked_chain_padlock_compliance && fullAssessment.sprinkler_valves_locked_chain_padlock_compliance !== 'not_applicable'),

                                  evacuation_routes: (fullAssessment.evacuation_routes_compliance && fullAssessment.evacuation_routes_compliance !== 'not_applicable') ||
                                                   (fullAssessment.escape_doors_fire_exit_signage_compliance && fullAssessment.escape_doors_fire_exit_signage_compliance !== 'not_applicable') ||
                                                   (fullAssessment.fire_escape_plans_displayed_compliance && fullAssessment.fire_escape_plans_displayed_compliance !== 'not_applicable'),

                                  housekeeping: (fullAssessment.housekeeping_compliance && fullAssessment.housekeeping_compliance !== 'not_applicable'),

                                  emergency_preparedness: (fullAssessment.emergency_preparedness_compliance && fullAssessment.emergency_preparedness_compliance !== 'not_applicable') ||
                                                        (fullAssessment.emergency_assembly_points_compliance && fullAssessment.emergency_assembly_points_compliance !== 'not_applicable') ||
                                                        (fullAssessment.emergency_contact_list_compliance && fullAssessment.emergency_contact_list_compliance !== 'not_applicable'),

                                  hazardous_chemicals: (fullAssessment.hazardous_chemicals_compliance && fullAssessment.hazardous_chemicals_compliance !== 'not_applicable') ||
                                                     (fullAssessment.chemical_storage_compliance && fullAssessment.chemical_storage_compliance !== 'not_applicable') ||
                                                     (fullAssessment.chemical_handling_procedures_compliance && fullAssessment.chemical_handling_procedures_compliance !== 'not_applicable'),

                                  general_condition: (fullAssessment.floors_compliance && fullAssessment.floors_compliance !== 'not_applicable') ||
                                                   (fullAssessment.walls_compliance && fullAssessment.walls_compliance !== 'not_applicable') ||
                                                   (fullAssessment.ceilings_compliance && fullAssessment.ceilings_compliance !== 'not_applicable') ||
                                                   (fullAssessment.windows_compliance && fullAssessment.windows_compliance !== 'not_applicable') ||
                                                   (fullAssessment.doors_compliance && fullAssessment.doors_compliance !== 'not_applicable') ||
                                                   (fullAssessment.stairways_compliance && fullAssessment.stairways_compliance !== 'not_applicable') ||
                                                   (fullAssessment.lighting_compliance && fullAssessment.lighting_compliance !== 'not_applicable')
                                };
                              }
                            } catch (error) {
                              console.error('Error loading assessment requirements:', error);
                            }

                            setAssessmentRequirements(requirements);
                            setActiveView('edit');
                          }}
                          className="flex items-center justify-center p-3 sm:p-2 text-gray-400 hover:text-blue-600 min-h-[48px] min-w-[48px] sm:min-h-[44px] sm:min-w-[44px] rounded-lg hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-5 h-5 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => onDeleteAssessment(assessment.id)}
                          className="flex items-center justify-center p-3 sm:p-2 text-gray-400 hover:text-red-600 min-h-[48px] min-w-[48px] sm:min-h-[44px] sm:min-w-[44px] rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
                        </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HSEAssessmentList;
